import type { BlockCandidate } from '@/types/ainotebook'
import { hasRealChars } from './noise'
import { median, avg } from './geom'

export function extractBlocksFromTesseract(data: any): BlockCandidate[] {
  const lines = Array.isArray(data?.lines) ? data.lines : []
  if (lines.length) {
    return lines
      .map((l: any) => {
        const text = String(l.text || '').replace(/\s+/g, ' ').trim()
        const bb = l.bbox
        const conf = Number(l.confidence ?? l.conf ?? NaN)
        if (!text || !bb) return null
        return {
          text,
          rect: { x: bb.x0, y: bb.y0, w: bb.x1 - bb.x0, h: bb.y1 - bb.y0 },
          confidence: Number.isFinite(conf) ? conf : undefined
        }
      })
      .filter(Boolean) as any
  }

  const words = Array.isArray(data?.words) ? data.words : []
  if (words.length) {
    const filtered = words
      .map((x: any) => {
        const t = String(x.text || '').trim()
        const c = Number(x.confidence ?? x.conf ?? NaN)
        if (!t || !hasRealChars(t)) return null
        return {
          text: t,
          conf: Number.isFinite(c) ? c : undefined,
          bbox: { x0: x.bbox.x0, y0: x.bbox.y0, x1: x.bbox.x1, y1: x.bbox.y1 }
        }
      })
      .filter(Boolean)
      .filter((x: any) => (x.conf ?? 0) >= 60)

    return buildLineBlocksWithConf(filtered as any)
  }

  if (typeof data?.tsv === 'string' && data.tsv.includes('\t')) {
    return blocksFromTsv(data.tsv)
  }

  return []
}

function blocksFromTsv(tsv: string): BlockCandidate[] {
  const rows = tsv.trim().split('\n')
  if (rows.length <= 1) return []

  const words: Array<{ text: string; conf?: number; bbox: { x0: number; y0: number; x1: number; y1: number } }> = []

  for (let i = 1; i < rows.length; i++) {
    const cols = rows[i].split('\t')
    if (cols.length < 12) continue
    if (cols[0] !== '5') continue

    const left = Number(cols[6])
    const top = Number(cols[7])
    const width = Number(cols[8])
    const height = Number(cols[9])
    const conf = Number(cols[10])
    const text = String(cols[11] || '').trim()

    if (!text) continue
    if (!hasRealChars(text)) continue
    if (!Number.isFinite(conf) || conf < 70) continue
    if (!Number.isFinite(left) || !Number.isFinite(top) || width <= 0 || height <= 0) continue

    words.push({ text, conf, bbox: { x0: left, y0: top, x1: left + width, y1: top + height } })
  }

  return buildLineBlocksWithConf(words)
}

function buildLineBlocksWithConf(
  words: Array<{ text: string; conf?: number; bbox: { x0: number; y0: number; x1: number; y1: number } }>
): BlockCandidate[] {
  if (!words.length) return []

  const items = words
    .map(w => ({
      text: w.text,
      conf: w.conf,
      x: w.bbox.x0,
      y: w.bbox.y0,
      w: w.bbox.x1 - w.bbox.x0,
      h: w.bbox.y1 - w.bbox.y0,
      cx: (w.bbox.x0 + w.bbox.x1) / 2,
      cy: (w.bbox.y0 + w.bbox.y1) / 2
    }))
    .sort((a, b) => a.cy - b.cy || a.x - b.x)

  const medianH = median(items.map(i => i.h)) || 14
  const lineThreshold = Math.max(6, medianH * 0.6)

  type Line = { words: typeof items; cy: number }
  const lines: Line[] = []

  for (const it of items) {
    const hit = lines.find(l => Math.abs(l.cy - it.cy) <= lineThreshold)
    if (!hit) lines.push({ words: [it], cy: it.cy })
    else {
      hit.words.push(it)
      hit.cy = (hit.cy * (hit.words.length - 1) + it.cy) / hit.words.length
    }
  }

  return lines
    .map(l => {
      const ws = l.words.sort((a, b) => a.x - b.x)
      const text = ws.map(x => x.text).join(' ').replace(/\s+/g, ' ').trim()
      const x0 = Math.min(...ws.map(x => x.x))
      const y0 = Math.min(...ws.map(x => x.y))
      const x1 = Math.max(...ws.map(x => x.x + x.w))
      const y1 = Math.max(...ws.map(x => x.y + x.h))
      const confs = ws.map(x => x.conf).filter((c): c is number => typeof c === 'number')
      return { text, rect: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }, confidence: avg(confs) }
    })
    .filter(b => b.text.length > 0 && b.rect.w > 8 && b.rect.h > 8)
}
